import React, { useState, useMemo, useEffect } from "react";
import {
  useFloating,
  autoUpdate,
  offset,
  flip,
  shift,
  useHover,
  useFocus,
  useDismiss,
  useRole,
  useInteractions,
  FloatingPortal,
  safePolygon,
  FloatingFocusManager,
} from "@floating-ui/react";
import themeGet from "@styled-system/theme-get";
import styled from "styled-components";
import Icon from "../Icon";
import { PropTypes } from "prop-types";
import {
  getFloatingUiRootElement,
  getFloatingUiZIndex,
  isRenderedInReactSelectMenu,
} from "../../utils/floatingUiHelpers";
import { layout, space } from "styled-system";
const DIRECTIONS_MAP = {
  topLeft: "top-start",
  top: "top",
  topRight: "top-end",
  left: "left",
  right: "right",
  bottomLeft: "bottom-start",
  bottom: "bottom",
  bottomRight: "bottom-end",
};

const Container = styled.div`
  ${space}
  ${layout}

  display: ${(props) => {
    if (props.display) {
      return props.display;
    }
    if (props.inlineBlock) {
      return "inline-block";
    }
    return "block";
  }};
  position: relative;
  overflow: visible;
`;

const TooltipControl = styled.div`
  border: none;
  background: none;
  padding: 0;
  cursor: help;
  font-size: 1em;
  /* iconDefault (not black) so the help/info trigger stays visible on dark
     surfaces; brand-blue linkText on active/hover reads in both themes. */
  color: ${(props) =>
    props.active
      ? themeGet("colors.linkText")(props)
      : themeGet("colors.iconDefault")(props)};
  transition: ${themeGet("transition.transitionDefault")};
  &:hover,
  &:focus {
    outline: 0;
    color: ${themeGet("colors.linkText")};
  }
`;

const StyledPopover = styled.div`
  font-size: ${themeGet("fontSizes.0")};
  line-height: ${themeGet("fontSizes.1")};
  font-weight: ${themeGet("fontWeights.1")};
  text-align: ${(props) => (props.textAlign ? props.textAlign : "left")};
  word-break: break-word;
  color: ${themeGet("colors.bodyText")};
  outline: 0;
  padding: ${themeGet("space.3")};
  border-radius: ${themeGet("radii.1")};
  width: ${(props) => (props.width ? props.width : "200px")};
  background: ${themeGet("colors.surfaceRaised")};
  border: 1px solid ${themeGet("colors.surfaceBorder")};
  box-shadow: ${themeGet("shadows.boxDefault")};
  user-select: ${(props) => (props.enableSelectAll ? "all" : "auto")};

  &.hack-for-legacy-tests {
    position: absolute;
    pointer-events: none;
    opacity: 0;
    visibility: hidden;
    height: 0;
    width: 0;
    padding: 0;
    overflow: hidden;
  }

  &.visible {
    opacity: 1;
    pointer-events: auto;
    visibility: visible;
  }

  & a {
    font-size: ${themeGet("fontSizes.0")};
  }

  &::before {
    content: "";
    z-index: 2;
    height: 0;
    width: 0;
    border-style: solid;
    border-width: 6px 6px 6px 0;
    border-color: transparent;
    border-right-color: ${themeGet("colors.surfaceRaised")};
    left: -6px;
    top: 50%;
    margin-top: -6px;
    position: absolute;
  }

  &::after {
    content: "";
    z-index: 1;
    position: absolute;
    border-color: transparent;
    border-right-color: ${themeGet("colors.surfaceBorder")};
    height: 0;
    width: 0;
    border-style: solid;
    border-width: 6px 6px 6px 0;
    left: -7px;
    top: 50%;
    margin-top: -6px;
  }

  &.top {
    &::before {
      left: 50%;
      top: auto;
      margin-top: 0;
      bottom: -9px;
      margin-left: -3px;
      transform: rotate(-90deg);
    }
    &::after {
      left: 50%;
      top: auto;
      margin-top: 0;
      bottom: -10px;
      margin-left: -3px;
      transform: rotate(-90deg);
    }
  }

  &.topRight,
  &.top-end {
    &::before {
      left: 1px;
      top: auto;
      margin-top: 0;
      bottom: -5px;
      margin-left: -6px;
      transform: rotate(-45deg);
      border-width: 5px 10px 5px 0;
    }
    &::after {
      left: 1px;
      top: auto;
      margin-top: 0;
      bottom: -6px;
      margin-left: -7px;
      transform: rotate(-45deg);
      border-width: 5px 10px 5px 0;
    }
  }

  &.bottomRight,
  &.bottom-end {
    &::before {
      left: 1px;
      bottom: auto;
      margin-top: 0;
      top: -5px;
      margin-left: -6px;
      transform: rotate(45deg);
      border-width: 5px 10px 5px 0;
    }
    &::after {
      left: 1px;
      bottom: auto;
      margin-top: 0;
      top: -6px;
      margin-left: -7px;
      transform: rotate(45deg);
      border-width: 5px 10px 5px 0;
    }
  }

  &.bottom {
    &::before {
      left: 50%;
      top: -9px;
      margin-top: 0;
      margin-left: -3px;
      transform: rotate(90deg);
    }
    &::after {
      left: 50%;
      top: -10px;
      margin-top: 0;
      margin-left: -3px;
      transform: rotate(90deg);
    }
  }

  &.bottomLeft,
  &.bottom-start {
    &::before {
      right: 1px;
      left: auto;
      bottom: auto;
      margin-top: 0;
      top: -5px;
      margin-right: -6px;
      transform: rotate(135deg);
      border-width: 5px 10px 5px 0;
    }
    &::after {
      right: 1px;
      left: auto;
      bottom: auto;
      margin-top: 0;
      top: -6px;
      margin-right: -7px;
      transform: rotate(135deg);
      border-width: 5px 10px 5px 0;
    }
  }

  &.left {
    &::before {
      left: auto;
      right: -6px;
      transform: rotate(180deg);
    }
    &::after {
      left: auto;
      right: -7px;
      transform: rotate(180deg);
      top: 50%;
      margin-top: -6px;
    }
  }

  &.topLeft,
  &.top-start {
    &::before {
      right: 1px;
      left: auto;
      top: auto;
      margin-top: 0;
      bottom: -5px;
      margin-right: -6px;
      transform: rotate(225deg);
      border-width: 5px 10px 5px 0;
    }
    &::after {
      right: 1px;
      left: auto;
      top: auto;
      margin-top: 0;
      bottom: -6px;
      margin-right: -7px;
      transform: rotate(225deg);
      border-width: 5px 10px 5px 0;
    }
  }
`;

/**
 * Prevents the browser from scrolling to the previously focused element
 * when a popover or tooltip closes — and skips focus entirely if it's offscreen.
 */
export function usePreventScrollOnRestoreFocus(enabled) {
  useEffect(() => {
    if (!enabled) {
      return;
    }
    const previouslyFocused = document.activeElement;

    if (!(previouslyFocused instanceof HTMLElement)) {
      return;
    }

    const originalFocus = previouslyFocused.focus.bind(previouslyFocused);

    // Check if element is in the viewport
    const isInViewport = isElementInViewport(previouslyFocused);

    if (!isInViewport) {
      // Skip restoring focus entirely
      previouslyFocused.focus = () => {};
    } else {
      // Patch focus to use preventScroll
      previouslyFocused.focus = function patchedFocus() {
        try {
          originalFocus({ preventScroll: true });
        } catch {
          originalFocus();
        }
      };
    }

    return () => {
      previouslyFocused.focus = originalFocus;
    };
  }, [enabled]);
}

function isElementInViewport(el) {
  const rect = el.getBoundingClientRect();
  return (
    rect.top >= 0 &&
    rect.left >= 0 &&
    rect.bottom <=
      (window.innerHeight || document.documentElement.clientHeight) &&
    rect.right <= (window.innerWidth || document.documentElement.clientWidth)
  );
}

export default function Popover({
  children,
  direction = "right",
  text,
  textAlign,
  width,
  enableSelectAll,
  variant,
  ariaLabel,
  inlineBlock,
  withFocusControl = true,
  offset: customOffset,
  headerAvatarSizing,
  disabled,
  ...props
}) {
  const [visible, setVisible] = useState(false);

  const middleware = useMemo(
    () => [
      offset(({ rects }) => {
        const defaultOffset = {
          mainAxis: 10,
          alignmentAxis: -rects.floating.width,
        };

        if (customOffset) {
          // For simple placements (right, left, top, bottom), use crossAxis instead of alignmentAxis
          const placement = DIRECTIONS_MAP[direction] || direction || "right";
          const isSimplePlacement = !placement.includes("-");

          if (isSimplePlacement && customOffset.alignmentAxis !== undefined) {
            return {
              ...defaultOffset,
              mainAxis:
                customOffset.mainAxis !== undefined
                  ? customOffset.mainAxis
                  : defaultOffset.mainAxis,
              crossAxis: customOffset.alignmentAxis,
            };
          }

          return {
            ...defaultOffset,
            ...customOffset,
          };
        }

        return defaultOffset;
      }),
      flip({ fallbackAxisSideDirection: "start" }),
      shift(),
    ],
    [direction, customOffset]
  );

  const { refs, floatingStyles, context } = useFloating({
    open: visible,
    onOpenChange: disabled ? () => {} : setVisible,
    placement: DIRECTIONS_MAP[direction] || direction || "right",
    whileElementsMounted: (reference, floating, update) =>
      autoUpdate(reference, floating, update, {
        ancestorScroll: true,
        ancestorResize: true,
        elementResize: true,
        layoutShift: true,
        animationFrame: false,
      }),
    middleware,
  });

  const hover = useHover(context, {
    move: false,
    handleClose: safePolygon(),
    delay: { open: 400, close: 0 },
  });
  const focus = useFocus(context);
  const dismiss = useDismiss(context);
  const role = useRole(context, { role: "tooltip" });

  const { getReferenceProps, getFloatingProps } = useInteractions([
    hover,
    focus,
    dismiss,
    role,
  ]);

  const triggerProps = useMemo(
    () => ({
      ...getReferenceProps({
        ref: refs.setReference,
      }),
      tabIndex: props.tabIndex !== undefined ? props.tabIndex : "0",
    }),
    [getReferenceProps, refs.setReference, props.tabIndex]
  );

  const directionClass = useMemo(
    () =>
      context.placement === DIRECTIONS_MAP[direction]
        ? direction
        : context.placement,
    [context.placement, direction]
  );

  const style = useMemo(() => {
    const baseStyle = {
      ...floatingStyles,
      zIndex: getFloatingUiZIndex(context.refs.reference),
    };

    // Add CSS-based positioning for Header Avatar sizing changes
    if (headerAvatarSizing) {
      const translateY = headerAvatarSizing === "large" ? -56 : -3;
      baseStyle.transform = `${baseStyle.transform || ""} translateY(${translateY}px)`;
    }

    return baseStyle;
  }, [floatingStyles, context.refs.reference, headerAvatarSizing]);

  const containsLinks =
    withFocusControl && refs.floating?.current?.querySelectorAll("a").length;

  const visiblePopoverClassName = useMemo(
    () => `Tooltip popover visible ${directionClass}`,
    [directionClass]
  );

  const floatingProps = useMemo(
    () =>
      getFloatingProps({
        ...props,
        className: `${props.className} ${visiblePopoverClassName}`,
      }),
    [getFloatingProps, props, visiblePopoverClassName]
  );

  usePreventScrollOnRestoreFocus(!visible);

  return (
    <Container
      inlineBlock={inlineBlock}
      {...props}
      {...triggerProps}
      aria-label={ariaLabel}
      aria-describedby={context.floatingId}
    >
      {variant === "tooltip" && (
        <TooltipControl active={visible} tabIndex="0">
          <Icon
            transform="grow-4"
            icon={["fas", "question-circle"]}
            fontSize="2"
          />
        </TooltipControl>
      )}

      {text &&
        (visible ? (
          <FloatingPortal
            root={getFloatingUiRootElement(context.refs.reference)}
            preserveTabOrder={true}
          >
            {containsLinks ? (
              <FloatingFocusManager
                context={context}
                modal={false}
                restoreFocus={false}
                initialFocus={
                  // If the popover is rendered in a React Select menu, don't focus the first element. Keep focus on select input else it will close the popover.
                  // Default to 0 to focus the first element if not rendered in a React Select menu.
                  isRenderedInReactSelectMenu(context.refs.reference) ? -1 : 0
                }
              >
                <StyledPopover
                  className={visiblePopoverClassName}
                  // oxlint-disable-next-line react/refs -- Floating UI setter bag
                  ref={refs.setFloating}
                  textAlign={textAlign}
                  width={width}
                  enableSelectAll={enableSelectAll}
                  {...floatingProps}
                  style={style}
                >
                  {text}
                </StyledPopover>
              </FloatingFocusManager>
            ) : (
              <StyledPopover
                className={visiblePopoverClassName}
                // oxlint-disable-next-line react/refs -- Floating UI setter bag
                ref={refs.setFloating}
                textAlign={textAlign}
                width={width}
                enableSelectAll={enableSelectAll}
                {...floatingProps}
                style={style}
              >
                {text}
              </StyledPopover>
            )}
          </FloatingPortal>
        ) : (
          /*
           * HACK: Fixing all the broken tests in teamform-app-ui is too time consuming
           * right this moment with a lot of the tests asserting against Popover items.
           * Rendering the markup even when closed but in a hidden state ensures that tests pass.
           * Ideally, we would update all the tests in teamform-app-ui to open the Popover
           * before assertion.
           **/
          <StyledPopover className="Tooltip popover hack-for-legacy-tests">
            {text}
          </StyledPopover>
        ))}

      {children}
    </Container>
  );
}

Popover.propTypes = {
  /** The element that requires the popover helper text. */
  children: PropTypes.element,
  /** Specifies the direction of the popover. Defaults to right if not specified */
  direction: PropTypes.oneOf([
    ...Object.keys(DIRECTIONS_MAP),
    ...Object.values(DIRECTIONS_MAP),
  ]),
  /** The text contained in the popover element */
  text: PropTypes.node,
  /** Specifies the alignment of the text inside the popover */
  textAlign: PropTypes.oneOf(["left", "right", "center"]),
  /** Specifies the width of the popover (you need to specify units, e.g. pixels, %). If you use % it will be a percentage of the width of the Popover container */
  width: PropTypes.string,
  /** Sets display property of popover tooltip to inline-block */
  inlineBlock: PropTypes.bool,
  /** Specifies the variant of the popover. */
  variant: PropTypes.oneOf(["tooltip"]),
  /** Specifies the system design theme. */
  theme: PropTypes.object,
  /** Specifies whether enable select all behaviour */
  enableSelectAll: PropTypes.bool,
  /** Accessible name rendered as aria-label on the tabbable trigger. Provide when children have no text of their own or text is not a string */
  ariaLabel: PropTypes.string,
  /** Provide a tab index for accessibilty, defaults to 0 */
  tabIndex: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  className: PropTypes.string,
  /** Render tooltip with focus control when there is link inside, defaults to true */
  withFocusControl: PropTypes.bool,
  /** Custom offset configuration for the popover positioning. Can be an object with mainAxis and/or alignmentAxis properties, or a function that returns an offset object. */
  offset: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
  /** Special prop for Header Avatar to apply CSS-based positioning adjustments when sizing changes dynamically */
  headerAvatarSizing: PropTypes.oneOf(["large", "default", "small"]),
  /** Disable the popover */
  disabled: PropTypes.bool,
};
